import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ViewChild,
  Inject,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  FormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';
import { Observable, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  take,
} from 'rxjs/operators';
import { AppConfig } from 'src/app/app-config';
import { Author, IAuthor } from 'src/app/core/models/i-author';
import { IDeliveryTarget } from 'src/app/core/models/i-delivery-target';
import { ITopic } from 'src/app/core/models/i-topic';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { IUserSettings } from '../../core/models/i-user-settings';
import { AuthorService } from '../../shared/services/author.service';
import { UserSettingsService } from '../../shared/services/user-settings.service';
import {
  ArticleStatus,
  GetArticleStatus,
} from '../shared/enums/article-status';
import { ContentTableType } from '../shared/enums/content-table-type';
import { ContentType, GetContentTypes } from '../shared/enums/content-type';
import { ArticleTopic } from '../../core/models/i-article-topic';
import {
  ContentFilterValues,
  IContentFilterValues,
} from '../../core/models/i-content-filter-values';
import { SearchArticleRequest } from '../../core/models/i-search-article-request';
import { ContentService } from '../shared/services/content.service';
import { LocalStorageService } from '../shared/services/helpers/local-storage.service';
import { getDateWithHourOffset } from '../shared/services/helpers/datetime-helper';
import { TopicService } from '../shared/services/topic.service';
import { FORM_FIELDS } from './constants/form-fields';
import { FORM_PATHS } from './constants/form-paths';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { ContentTypes } from 'src/app/shared/enums/content-types';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { FeatureFlagsService } from 'src/app/core/feature-flags/feature-flag.service';
import { Features } from 'src/app/core/feature-flags/features';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { IDashboardContent } from 'src/app/core/models/i-dashboard-content';
import { UrlService } from 'src/app/shared/services/url.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CitywireInternalAccountsService } from '@tpapa/citywire-internal-accounts-angular';
import { ITopicWithTranslations } from 'src/app/core/models/i-topic-with-translations';

class ContentTableComponentBase {
  constructor() {}
}

const ContentTableComponentMixinBase = WithProcessing(
  ContentTableComponentBase
);

@Component({
  selector: 'app-content-table',
  templateUrl: './content-table.component.html',
  styleUrls: ['./content-table.component.scss'],
})
export class ContentTableComponent
  extends ContentTableComponentMixinBase
  implements OnInit, OnDestroy
{
  @Input() contentTableType: ContentTableType;
  @Input() disableWebsiteFilter = false;
  @Input() disableStatusFilter = false;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  subscriptions: Subscription[] = [];
  userSettings: IUserSettings;
  dataSource = new MatTableDataSource<IDashboardContent>();
  tableId = '';
  cmsUrl = '';

  statusList = [];
  websiteList = [];
  contentTypeList: string[] = [];

  FORM_FIELDS = FORM_FIELDS;
  FORM_PATHS = FORM_PATHS;

  // Pagination Variables
  totalCount = 0;
  pageNumbers = [1, 2, 3, 4, 5];
  maxNumberOfPageButtons = 5;
  pageTracker = 1;
  pagingOptions: string[] = ['5', '10', '20', '50'];
  pageSize = parseInt(this.pagingOptions[0], 10);
  defaultPageSize = 20;
  DISPLAYED_COLUMNS: string[] = [
    'status',
    'contentType',
    'topics',
    'website',
    'author',
    'lastModifiedDateTime',
  ];

  // Filter Variables
  hoursBack: number = null;
  topicResults: ITopicWithTranslations[] = [];
  authorResults: IAuthor[] = [];

  articleStatus: ArticleStatus[] = [];
  authors: Author[] = [];
  deliveryTarget: string[] = [];
  contentType: ContentType[] = [];
  topics: ArticleTopic[] = [];
  extendedSearch = true;

  debounceTime = 400;

  MODIFIED_TIME_SELECTION_LIST = [
    { id: 1, name: 'Last hour' },
    { id: 2, name: 'Last 24 hours' },
    { id: 3, name: 'Last week' },
    { id: 4, name: 'Last month' },
    { id: 5, name: 'Any time' },
  ];

  selectedTopicList = [];
  selectedAuthorList = [];

  contentStatus = ContentStatus;
  contentTypeEnum = ContentType;

  filterForm: UntypedFormGroup = this.fb.group({
    [this.FORM_FIELDS.STATUS_FILTER]: [''],
    [this.FORM_FIELDS.WEBSITES_FILTER]: [''],
    [this.FORM_FIELDS.CONTENT_TYPE_FILTER]: [''],
    [this.FORM_FIELDS.MODIFIED_FILTER]: this.MODIFIED_TIME_SELECTION_LIST[4].id,
    [this.FORM_FIELDS.TOPIC_FILTER]: this.selectedTopicList,
    [this.FORM_FIELDS.AUTHOR_FILTER]: this.selectedAuthorList,
    [this.FORM_FIELDS.CONTENT_SEARCH_FILTER]: '',
  });

  filterValues = {
    status: [],
    contentType: [],
    topic: [],
    websites: [],
    author: [],
    modified: [],
    search: '',
  };

  pageSizeFilter = new UntypedFormControl();
  searchTopicText = new UntypedFormControl();
  selectedTopicIds = new UntypedFormControl();
  selectedAuthorIds = new UntypedFormControl();
  searchAuthorText = new UntypedFormControl();

  storedFilterValues: IContentFilterValues;
  userDefaultWebsite = [];
  allDeliveryTargets: IDeliveryTarget[] = [];
  currentPageNumber: number;
  currentSearchParam: string;

  constructor(
    @Inject('Window') private window: Window,
    private contentService: ContentService,
    private userSettingsService: UserSettingsService,
    private localStorageService: LocalStorageService,
    private getDeliveryTargetsService: DeliveryTargetsService,
    private accountsService: CitywireInternalAccountsService,
    private topicService: TopicService,
    private authorService: AuthorService,
    private fb: FormBuilder,
    private appConfig: AppConfig,
    private featureFlagsService: FeatureFlagsService,
    private urlService: UrlService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.tableId = this.contentTableType.toString();
    this.cmsUrl = this.appConfig.cmsUrl;
    this.currentPageNumber = parseInt(
      this.route.snapshot?.queryParams?.['page']
    );
    if (typeof this.currentPageNumber === 'number') {
      this.pageTracker = this.currentPageNumber;
    }
    this.currentSearchParam = this.route.snapshot?.queryParams?.['search'];

    this.populateFiltersFromLocalStorage();

    this.filterValues.topic = this.filterForm.get(
      this.FORM_PATHS.TOPIC_FILTER()
    ).value;
    this.filterValues.author = this.filterForm.get(
      this.FORM_PATHS.AUTHOR_FILTER()
    ).value;
    this.contentTypeList = GetContentTypes();
    this.statusList = GetArticleStatus();
    this.initialiseComponent();
  }

  loadContentData(): void {
    const request = this.buildGetRequest();
    const getContentFiltered$ = this.contentService.getFiltered$(request);

    const contentServiceSubscription = this.process$(
      getContentFiltered$
    ).subscribe((data) => {
      this.dataSource.paginator = this.paginator;
      this.dataSource.filterPredicate = (): boolean => true;
      this.dataSource.data = data.contentResults;
      this.totalCount = data.totalMatches;
      this.dataSource.sort = this.sort;
      this.dataSource.filter = JSON.stringify(this.storedFilterValues);
      this.populatePageButtonArray();

      this.pageSizeFilter.setValue(this.pageSize.toString(), {
        emitEvent: false,
      });

      this.setSearchParam();

      this.setCurrentDataSourceFilter();
    });
    this.subscriptions.push(contentServiceSubscription);
  }

  getHeader(): string {
    let heading = 'Content';
    switch (this.contentTableType) {
      case ContentTableType.MyContent:
        heading = 'My Content';
        break;
      case ContentTableType.TeamContent:
        heading = 'Team Content';
        break;
      case ContentTableType.ColumnistContent:
        heading = 'Columnist Content';
        break;
      case ContentTableType.AllContent:
        heading = 'All Content';
        break;
      case ContentTableType.SubEditorContent:
        heading = "Sub-editor's Content";
        break;
    }
    return heading;
  }

  getTargetCodeFriendlyName(code: string): string {
    const target = this.allDeliveryTargets.filter((x) => x.code === code);

    return target && target[0]?.friendlyName;
  }

  // Filter handlers
  getSearchValue(): any {
    return this.filterForm.get(this.FORM_PATHS.CONTENT_SEARCH_FILTER()).value;
  }

  setSearchValue(value: string): void {
    if (value === '' && this.getSearchValue() !== '') {
      const queryParams: Params = { page: null, search: null };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      });
      this.currentPageNumber = 1;
    }
    this.filterForm
      .get(this.FORM_PATHS.CONTENT_SEARCH_FILTER())
      .setValue(value);
  }

  getTopicList(value: string): void {
    this.topicService
      .getFiltered$(null, value, 1000, false, false)
      .subscribe((x) => {
        const result = x
          .filter((y) => y.parentId !== null)
          .filter((y) => !this.selectedTopicList.some((z) => z.id === y.id));
        this.topicResults = [...this.selectedTopicList, ...result];
      });
  }

  getAuthorList(value: string): void {
    this.authorService
      .getPagedFilteredBySearchText$(value, 1, 10)
      .subscribe((x) => {
        const result = x.results.filter(
          (y) => !this.selectedAuthorList.some((z) => z.name === y.name)
        );

        this.authorResults = [...this.selectedAuthorList, ...result];
      });
  }

  updateAuthorList(authorId, isAdded): void {
    if (isAdded) {
      this.selectedAuthorList.push(
        this.authorResults.find((x) => x.id === authorId)
      );
    } else {
      this.selectedAuthorList = this.selectedAuthorList.filter(
        (x) => x.id !== authorId
      );
    }

    this.filterForm
      .get(this.FORM_PATHS.AUTHOR_FILTER())
      .setValue(this.selectedAuthorList);
  }

  updateTopicList(topicId, isAdded): void {
    if (isAdded) {
      this.selectedTopicList.push(
        this.topicResults.find((x) => x.id === topicId)
      );
    } else {
      this.selectedTopicList = this.selectedTopicList.filter(
        (x) => x.id !== topicId
      );
    }

    this.filterForm
      .get(this.FORM_PATHS.TOPIC_FILTER())
      .setValue(this.selectedTopicList);
  }

  resetFilters(): void {
    this.storedFilterValues = this.localStorageService.defaultContentFilters(
      this.tableId,
      this.storedFilterValues.pageSize
    );

    if (this.userDefaultWebsite.length > 0) {
      this.filterForm
        .get(this.FORM_PATHS.WEBSITES_FILTER())
        .setValue(this.userDefaultWebsite as any);
      this.storedFilterValues.website = this.userDefaultWebsite;
      this.localStorageService.setContentFilters(
        this.storedFilterValues,
        this.tableId
      );
    }

    this.populateFiltersFromLocalStorage();
  }

  hasFiltersSet(): boolean {
    const hasStatusFilter =
      this.filterForm.get(this.FORM_PATHS.STATUS_FILTER()).value.length > 0;
    const hasContentTypeFilter =
      this.filterForm.get(this.FORM_PATHS.CONTENT_TYPE_FILTER()).value.length >
      0;
    const hasTopicFilter =
      this.filterForm.get(this.FORM_PATHS.TOPIC_FILTER()).value.length > 0;
    const hasWebsiteFilter =
      JSON.stringify(
        this.filterForm.get(this.FORM_PATHS.WEBSITES_FILTER()).value
      ) !== JSON.stringify(this.userDefaultWebsite);
    const hasAuthorFilter =
      this.filterForm.get(this.FORM_PATHS.AUTHOR_FILTER()).value.length > 0;
    const hasModifiedFilter =
      this.filterForm.get(this.FORM_PATHS.MODIFIED_FILTER()).value !==
      this.MODIFIED_TIME_SELECTION_LIST[4].id;

    return (
      hasStatusFilter ||
      hasContentTypeFilter ||
      hasWebsiteFilter ||
      hasTopicFilter ||
      hasAuthorFilter ||
      hasModifiedFilter
    );
  }

  isPublished(status: string): boolean {
    return status === ArticleStatus[ArticleStatus.Published];
  }

  setCurrentDataSourceFilter(): void {
    let setFilters: IContentFilterValues =
      this.localStorageService.getContentFilters(this.tableId);
    if (!setFilters) {
      setFilters = new ContentFilterValues(
        [],
        [],
        [],
        [],
        [],
        5,
        this.defaultPageSize
      );
    }
    setFilters.status = this.filterForm.get(this.FORM_PATHS.STATUS_FILTER())
      .value as any;
    setFilters.contentType = this.filterForm.get(
      this.FORM_PATHS.CONTENT_TYPE_FILTER()
    ).value as any;
    setFilters.topic = this.filterForm.get(
      this.FORM_PATHS.TOPIC_FILTER()
    ).value;
    setFilters.website = this.filterForm.get(this.FORM_PATHS.WEBSITES_FILTER())
      .value as any;
    setFilters.author = this.filterForm.get(
      this.FORM_PATHS.AUTHOR_FILTER()
    ).value;
    setFilters.time = this.filterForm.get(
      this.FORM_PATHS.MODIFIED_FILTER()
    ).value;
    setFilters.pageSize = this.pageSize;

    this.storedFilterValues = this.localStorageService.setContentFilters(
      setFilters,
      this.tableId
    );
  }

  populateFiltersFromLocalStorage(): void {
    this.storedFilterValues = this.localStorageService.getContentFilters(
      this.tableId
    );
    if (!this.storedFilterValues) {
      this.storedFilterValues = new ContentFilterValues(
        [],
        [],
        [],
        [],
        [],
        5,
        this.defaultPageSize
      );
    }

    this.filterForm
      .get(this.FORM_PATHS.WEBSITES_FILTER())
      .setValue(this.storedFilterValues.website as any);
    this.filterForm
      .get(this.FORM_PATHS.STATUS_FILTER())
      .setValue(this.storedFilterValues.status as any);
    this.filterForm
      .get(this.FORM_PATHS.CONTENT_TYPE_FILTER())
      .setValue(this.storedFilterValues.contentType as any);
    this.filterForm
      .get(this.FORM_PATHS.TOPIC_FILTER())
      .setValue(this.storedFilterValues.topic);
    this.filterForm
      .get(this.FORM_PATHS.AUTHOR_FILTER())
      .setValue(this.storedFilterValues.author);
    this.filterForm
      .get(this.FORM_PATHS.MODIFIED_FILTER())
      .setValue(this.storedFilterValues.time);
    this.pageSize = this.storedFilterValues.pageSize || this.defaultPageSize;

    this.filterValues.status = this.filterForm.get(
      this.FORM_PATHS.STATUS_FILTER()
    ).value as any;
    this.filterValues.contentType = this.filterForm.get(
      this.FORM_PATHS.CONTENT_TYPE_FILTER()
    ).value as any;
    this.filterValues.topic = this.filterForm.get(
      this.FORM_PATHS.TOPIC_FILTER()
    ).value;
    this.filterValues.websites = this.filterForm.get(
      this.FORM_PATHS.WEBSITES_FILTER()
    ).value as any;
    this.filterValues.author = this.filterForm.get(
      this.FORM_PATHS.AUTHOR_FILTER()
    ).value;
    this.filterValues.modified = this.filterForm.get(
      this.FORM_PATHS.MODIFIED_FILTER()
    ).value as any;
    this.filterValues.search = this.filterForm.get(
      this.FORM_PATHS.CONTENT_SEARCH_FILTER()
    ).value;

    this.searchTopicText.setValue('');
    this.selectedTopicList = this.storedFilterValues.topic;
    this.selectedTopicIds.setValue(
      this.storedFilterValues?.topic.length > 0
        ? this.storedFilterValues.topic.map((x) => x.id)
        : []
    );

    this.searchAuthorText.setValue('');
    this.selectedAuthorList = this.storedFilterValues.author;
    this.selectedAuthorIds.setValue(
      this.storedFilterValues?.author.length > 0
        ? this.storedFilterValues.author.map((x) => x.id)
        : []
    );
  }

  initialiseComponent(): void {
    this.getDeliveryTargetsService
      .getAll$()
      .pipe(
        switchMap((allDeliveryTargets) => {
          this.allDeliveryTargets = allDeliveryTargets;
          this.websiteList = allDeliveryTargets
            .filter((website) => website.isTierOneSite)
            .map((x) => x.friendlyName);

          return this.accountsService.currentUser.pipe(
            take(1),
            switchMap((currentUser) =>
              this.userSettingsService.getByUserName$(
                currentUser.username.toLowerCase()
              )
            )
          );
        })
      )
      .subscribe((settings) => {
        this.userSettings = settings;
        if (this.tableId !== ContentTableType.AllContent.toString()) {
          const defaultWebsites = this.allDeliveryTargets
            .filter((x) => (settings.defaultDeliveryTargetCodes ?? []).includes(x.code))
            .sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));

          defaultWebsites.map((x) =>
            this.userDefaultWebsite.push(x.friendlyName)
          );

          if (!this.hasFiltersSet()) {
            this.filterForm
              .get(this.FORM_PATHS.WEBSITES_FILTER())
              .setValue(this.userDefaultWebsite as any);
          }
        }

        this.setupFilters();
        this.setSearchValue(
          this.currentSearchParam ? this.currentSearchParam : ''
        );
        this.selectPage(this.currentPageNumber ? this.currentPageNumber : 1);

        // Pre fill the dropdowns for authors and topics
        this.getTopicList('');
        this.getAuthorList('');
      });
  }

  setupFilters(): void {
    const filterFormSubscription = this.filterForm.valueChanges
      .pipe(debounceTime(this.debounceTime), distinctUntilChanged())
      .subscribe(() => {
        this.filterValues.status = this.filterForm.get(
          this.FORM_PATHS.STATUS_FILTER()
        ).value as any;
        this.filterValues.contentType = this.filterForm.get(
          this.FORM_PATHS.CONTENT_TYPE_FILTER()
        ).value as any;
        this.filterValues.topic = this.filterForm.get(
          this.FORM_PATHS.TOPIC_FILTER()
        ).value;
        this.filterValues.websites = this.filterForm.get(
          this.FORM_PATHS.WEBSITES_FILTER()
        ).value as any;
        this.filterValues.author = this.filterForm.get(
          this.FORM_PATHS.AUTHOR_FILTER()
        ).value;
        this.filterValues.modified = this.filterForm.get(
          this.FORM_PATHS.MODIFIED_FILTER()
        ).value as any;
        this.filterValues.search = this.filterForm.get(
          this.FORM_PATHS.CONTENT_SEARCH_FILTER()
        ).value;

        this.selectPage(this.currentPageNumber ? this.currentPageNumber : 1);
      });

    const authorSearchSubscription = this.searchAuthorText.valueChanges
      .pipe(debounceTime(this.debounceTime), distinctUntilChanged())
      .subscribe((x) => {
        this.getAuthorList(x);
      });
    this.subscriptions.push(authorSearchSubscription);

    const topicSearchSubscription = this.searchTopicText.valueChanges
      .pipe(debounceTime(this.debounceTime), distinctUntilChanged())
      .subscribe((x) => this.getTopicList(x));
    this.subscriptions.push(topicSearchSubscription);

    const pageSizeSubscription = this.pageSizeFilter.valueChanges.subscribe(
      (x) => this.selectPageSize(x)
    );

    if (this.disableWebsiteFilter) {
      this.filterForm.get(this.FORM_PATHS.WEBSITES_FILTER()).disable();
    }

    if (this.disableStatusFilter) {
      this.filterForm.get(this.FORM_PATHS.STATUS_FILTER()).disable();
    }

    this.subscriptions.push(
      filterFormSubscription,
      authorSearchSubscription,
      topicSearchSubscription,
      pageSizeSubscription
    );
  }

  buildGetRequest(): SearchArticleRequest {
    const deliveryCodes = this.allDeliveryTargets.filter((x) =>
      this.filterForm
        .get(this.FORM_PATHS.WEBSITES_FILTER())
        .value.includes(x.friendlyName)
    );
    const deliveryTarget = deliveryCodes.map((x) => x.code);
    this.getModifiedBounds(
      this.filterForm.get(this.FORM_PATHS.MODIFIED_FILTER()).value
    );

    const request = new SearchArticleRequest();
    request.pageNumber = this.pageTracker;
    request.pageSize = this.pageSize;
    request.lastModifiedLowerBound = this.hoursBack
      ? getDateWithHourOffset(-this.hoursBack)
      : null;
    request.searchText = this.filterForm.get(
      this.FORM_PATHS.CONTENT_SEARCH_FILTER()
    ).value;
    request.articleStatuses = (
      this.filterForm.get(this.FORM_PATHS.STATUS_FILTER()).value as any
    ).map((x) => x.split(' ').join('').replaceAll('-', ''));
    request.filterOnAuthors = this.filterForm.get(
      this.FORM_PATHS.AUTHOR_FILTER()
    ).value;

    request.deliveryTargetCodeFilter = deliveryTarget;
    request.contentTypeFilter = (
      this.filterForm.get(this.FORM_PATHS.CONTENT_TYPE_FILTER()).value as any
    ).map((x) => x.split(' ').join(''));
    request.topicFilter = this.filterForm.get(
      this.FORM_PATHS.TOPIC_FILTER()
    ).value;
    request.extendedSearch = this.extendedSearch || null;

    // Handling different table types
    switch (this.contentTableType) {
      case ContentTableType.MyContent:
        request.filterOnStartedEditingBy = this.userSettings.user;
        request.filterOnCreatedBy = this.userSettings.user;
        break;
      case ContentTableType.TeamContent:
        request.deliveryTargetCodeFilter =
          this.userSettings.defaultDeliveryTargetCodes;
        break;
      case ContentTableType.ColumnistContent:
        request.hideSpikedContent = true;
        request.onlyReturnColumnistContent = true;
        break;
      case ContentTableType.SubEditorContent:
        request.articleStatuses = [
          ContentStatus.WAITING_FOR_SUBEDITING,
          ContentStatus.SUBEDITING,
        ];
    }

    return request;
  }

  // pagination handlers
  hasPreviousPage(): boolean {
    return this.pageTracker > 1;
  }

  hasNextPage(): boolean {
    return this.pageTracker < Math.ceil(this.totalCount / this.pageSize);
  }

  loadNextPage(): void {
    this.pageTracker = this.pageTracker + 1;
    this.setPageParam();
    this.loadContentData();
  }

  loadPreviousPage(): void {
    this.pageTracker = this.pageTracker - 1;
    this.setPageParam();
    this.loadContentData();
  }

  selectPage(index: number): void {
    this.pageTracker = index;
    this.setPageParam();
    this.loadContentData();
  }

  selectPageSize(selectedPageSize: string): void {
    this.pageSize = parseInt(selectedPageSize);
    this.selectPage(1);
  }

  populatePageButtonArray(): void {
    const numberOfPagesToShow = 5;

    if (this.totalCount === 0) {
      this.pageNumbers = [];
      return;
    }
    const maxPage = Math.ceil(this.totalCount / this.pageSize);

    const currentPageIndex = this.pageTracker;

    const start = currentPageIndex - 3 <= 0 ? 1 : currentPageIndex - 2;
    let end = start + (numberOfPagesToShow - 1);
    if (start + (numberOfPagesToShow - 1) > maxPage) {
      end = maxPage;
    }

    this.pageNumbers = [];
    for (let i = start; i <= end; i++) {
      this.pageNumbers.push(i);
    }
  }

  isActivePage(currentIndex): boolean {
    return this.pageTracker === currentIndex;
  }

  getModifiedBounds(value): void {
    if (this.storedFilterValues) {
      this.storedFilterValues.time = value;
    }

    switch (value) {
      // Last hour
      case 1: {
        this.hoursBack = 1;
        break;
      }
      // Last 24 hours
      case 2: {
        this.hoursBack = 24;
        break;
      }
      // Last week
      case 3: {
        this.hoursBack = 24 * 7;
        break;
      }
      // Last month
      case 4: {
        this.hoursBack = 24 * 28;
        break;
      }
      default: {
        this.hoursBack = null;
        break;
      }
    }
  }

  isEnabledForContentType$(contentType: ContentType): Observable<boolean> {
    return this.featureFlagsService.isEnabledForContentType$(contentType);
  }

  getFeatureForContentType(contentType: ContentType) {
    return this.featureFlagsService.getFeatureForContentType(contentType);
  }

  getRouteForContentType(
    contentType: string,
    contentId: string,
    state: ContentStatus
  ) {
    return this.featureFlagsService.getRouteForContentType(
      contentType,
      contentId,
      state
    );
  }

  setPageParam() {
    if (this.pageTracker > 1) {
      const queryParams: Params = { page: this.pageTracker };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      });
    } else if ((this.pageTracker = 1)) {
      const queryParams: Params = { page: null };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      });
    }
  }

  setSearchParam() {
    if (this.getSearchValue() !== '') {
      const queryParams: Params = { search: this.getSearchValue() };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      });
    } else {
      const queryParams: Params = { search: null };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      });
    }
  }

  isGallery(contentType: ContentType): boolean {
    return contentType == ContentType.Gallery;
  }

  getUrl(status, versionId, deliveryTargetCode, url): string {
    if (status === ContentStatus.PUBLISHED) {
      return url;
    }
    return this.urlService.getPreviewUrl(versionId, deliveryTargetCode);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
